import { FormEvent, useEffect, useState } from "react";
import { Copy, KeyRound, RefreshCw, ShieldOff } from "lucide-react";
import { Modal } from "./Modal";
import { request } from "./FunctionalApp";

async function api(path: string, init: RequestInit = {}) {
  const response = await request(path, init);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(body.error || "Unable to save").replaceAll("_", " "));
  return body;
}

export function SupportAccessWorkspace({ tickets = [] }: { tickets?: any[] }) {
  const [grants, setGrants] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({ ticketId: "", minutes: 60, scopes: ["ticket"], permissions: ["read"] });
  async function load() {
    try { setGrants(await api("/api/support-grants")); }
    catch (error: any) { setMessage(error.message); }
  }
  useEffect(() => { load(); }, []);
  const active = (grant: any) => !grant.revokedAt && new Date(grant.expiresAt) > new Date();
  function toggle(kind: "scopes" | "permissions", value: string) {
    const values = form[kind];
    setForm({ ...form, [kind]: values.includes(value) ? values.filter(x => x !== value) : [...values, value] });
  }
  async function create(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    try {
      const result = await api("/api/support-grants", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(form) });
      setCode(result.code);
      setShow(false);
      await load();
    } catch (error: any) { setMessage(error.message); }
  }
  async function revoke(id: string) {
    try {
      await api(`/api/support-grants/${id}`, { method: "DELETE" });
      setMessage("Support access revoked immediately.");
      await load();
    } catch (error: any) { setMessage(error.message); }
  }
  return <section className="func-panel support-access">
    <div className="func-table-head"><div><h2>Temporary TaskRoute support access</h2><p>Create a time-limited code tied to a support ticket. You control exactly what support may see or change.</p></div><div><button className="secondary" onClick={load}><RefreshCw />Refresh</button><button onClick={() => { setShow(!show); setCode(""); }}><KeyRound />Create access code</button></div></div>
    {message ? <div className="func-admin-note">{message}</div> : null}
    {code ? <div className="support-code"><b>Copy this one-time code now</b><code>{code}</code><button type="button" onClick={() => navigator.clipboard.writeText(code)}><Copy />Copy code</button><small>TaskRoute cannot display this code again. Send it only to the authorised support person working on the named ticket.</small></div> : null}
    {show && (
      <Modal title="Create support access code" onClose={() => { setShow(false); setCode(""); }}>
        <form onSubmit={create}>
          <label>Support ticket
            <select required value={form.ticketId} onChange={e => setForm({ ...form, ticketId: e.target.value })}>
              <option value="">Choose a ticket…</option>
              {tickets.map((ticket: any) => <option key={ticket.id} value={ticket.id}>{ticket.number || ticket.id} · {ticket.title || ticket.subject}</option>)}
            </select>
          </label>
          <label>Expires after
            <select value={form.minutes} onChange={e => setForm({ ...form, minutes: Number(e.target.value) })}>
              <option value={15}>15 minutes</option><option value={30}>30 minutes</option>
              <option value={60}>1 hour</option><option value={120}>2 hours</option><option value={480}>8 hours</option>
            </select>
          </label>
          <fieldset><legend>Allowed areas</legend>
            {[["ticket","This ticket"],["maintenance","Maintenance"],["inventory","Inventory"],["dispatch","Dispatch"],["appointments","Appointments"]].map(([value,label]) => <label key={value} className="check-row"><input type="checkbox" checked={form.scopes.includes(value)} onChange={() => toggle("scopes", value)} />{label}</label>)}
          </fieldset>
          <fieldset><legend>Allowed actions</legend>
            {[["read","View"],["comment","Add support notes"],["diagnose","Run diagnostics"]].map(([value,label]) => <label key={value} className="check-row"><input type="checkbox" checked={form.permissions.includes(value)} onChange={() => toggle("permissions", value)} />{label}</label>)}
          </fieldset>
          <button disabled={!form.ticketId || !form.scopes.length || !form.permissions.length}>Generate one-time code</button>
        </form>
      </Modal>
    )}
    <div className="support-grant-list">{grants.length ? grants.map(grant => <article key={grant.id}><span><b>{grant.ticketNumber || grant.ticketId} · {grant.ticketTitle || "Support ticket"}</b><small>{Array.isArray(grant.scopes) ? grant.scopes.join(", ") : String(grant.scopes)} · {Array.isArray(grant.permissions) ? grant.permissions.join(", ") : String(grant.permissions)}</small></span><span><b>{active(grant) ? "Active" : grant.revokedAt ? "Revoked" : "Expired"}</b><small>Expires {new Date(grant.expiresAt).toLocaleString("en-GB")}</small></span>{active(grant) ? <button className="secondary" onClick={() => revoke(grant.id)}><ShieldOff />Revoke now</button> : null}</article>) : <p className="func-empty">No support access has been granted.</p>}</div>
  </section>;
}
